import { OrderCard } from "./ToolsMobileCards";
import { useToolsMobile } from "./ToolsLayout";
import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { Search } from "@mui/icons-material";
import { Link, useSearchParams } from "react-router-dom";
import { dateKey } from "../../utils/listData";
import ToolsLayout, { ListFooter, MarketStatus, RefreshTools, ToolsState } from "./ToolsLayout";
import useToolsData from "./useToolsData";
import { money, orderTotal, toolsDate } from "./toolsData";
import ExportExcelButton from "../lists/ExportExcelButton";

const columns = [
  { label: "Order ID", value: (row) => row.orderId },
  { label: "Customer", value: (row) => row.shippingAddress?.fullName },
  { label: "Phone", value: (row) => row.shippingAddress?.phone },
  { label: "Order status", value: (row) => row.orderStatus },
  { label: "Payment status", value: (row) => row.paymentStatus },
  { label: "Total (INR)", value: (row) => orderTotal(row) },
  { label: "Date (IST)", value: (row) => dateKey(row.createdOn) },
];
export default function ToolsOrders() {
  const mobile = useToolsMobile();
  const query = useToolsData("orders");
  const [params, setParams] = useSearchParams(),
    [page, setPage] = useState(1);
  const search = params.get("search") || "",
    status = params.get("status") || "",
    payment = params.get("paymentStatus") || "",
    start = params.get("from") || "",
    end = params.get("to") || "";
  const invalidDate = Boolean(start && end && start > end);
  const setFilter = (key, value) => {
    const next = new URLSearchParams(params);
    value ? next.set(key, value) : next.delete(key);
    setParams(next, { replace: true });
    setPage(1);
  };
  const filtered = useMemo(
    () =>
      [...(query.data || [])]
        .filter(
          (row) =>
            (!search ||
              `${row.orderId || ""} ${row.shippingAddress?.fullName || ""} ${row.shippingAddress?.phone || ""}`
                .toLowerCase()
                .includes(search.toLowerCase())) &&
            (!status || row.orderStatus === status) &&
            (!payment || row.paymentStatus === payment) &&
            (!start || dateKey(row.createdOn) >= start) &&
            (!end || (dateKey(row.createdOn) && dateKey(row.createdOn) <= end)),
        )
        .sort((a, b) => new Date(b.createdOn || 0) - new Date(a.createdOn || 0)),
    [query.data, search, status, payment, start, end],
  );
  useEffect(() => {
    setPage((current) => Math.min(current, Math.max(1, Math.ceil(filtered.length / 10))));
  }, [filtered.length]);
  const reset = () => {
    setParams({}, { replace: true });
    setPage(1);
  };
  const hasFilters = Boolean(search || status || payment || start || end);
  return (
    <ToolsLayout
      title="Tools orders"
      description="Review new orders, confirm shipping, and track fulfilment."
      actions={<RefreshTools onClick={query.refetch} loading={query.isFetching} />}
    >
      <section className="tools-panel">
        <div className="tools-panel-heading">
          <div>
            <Typography variant="h6" component="h2">
              Order queue
            </Typography>
            <p>
              {query.isLoading
                ? "Loading orders…"
                : query.isError
                  ? "Data unavailable"
                  : `${filtered.length} matching orders · ${(query.data || []).filter((row) => row.orderStatus === "pending").length} awaiting approval overall`}
            </p>
          </div>
          <ExportExcelButton
            columns={columns}
            filename="silkcart-tools-orders"
            sheetName="Orders"
            disabled={query.isLoading || query.isError || invalidDate}
            loadRows={async () => filtered}
          />
        </div>
        <div className="tools-filters">
          <TextField
            size="small"
            label="Search order, customer or phone"
            value={search}
            onChange={(event) => setFilter("search", event.target.value)}
            sx={{ flex: 1, minWidth: 240 }}
            InputProps={{ startAdornment: <Search fontSize="small" sx={{ mr: 1 }} /> }}
          />
          <TextField
            select
            size="small"
            label="Order status"
            value={status}
            onChange={(event) => setFilter("status", event.target.value)}
            sx={{ minWidth: 160 }}
          >
            <MenuItem value="">All orders</MenuItem>
            {["pending", "accepted", "shipped", "delivered", "rejected", "cancelled"].map(
              (value) => (
                <MenuItem key={value} value={value}>
                  {value === "pending"
                    ? "Awaiting approval"
                    : value.charAt(0).toUpperCase() + value.slice(1)}
                </MenuItem>
              ),
            )}
          </TextField>
          <TextField
            select
            size="small"
            label="Payment status"
            value={payment}
            onChange={(event) => setFilter("paymentStatus", event.target.value)}
            sx={{ minWidth: 160 }}
          >
            <MenuItem value="">All payments</MenuItem>
            {["pending", "processing", "paid", "rejected"].map((value) => (
              <MenuItem key={value} value={value}>
                {value.charAt(0).toUpperCase() + value.slice(1)}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            size="small"
            type="date"
            label="From date"
            value={start}
            onChange={(event) => setFilter("from", event.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            size="small"
            type="date"
            label="To date"
            value={end}
            onChange={(event) => setFilter("to", event.target.value)}
            InputLabelProps={{ shrink: true }}
            error={invalidDate}
            helperText={invalidDate ? "End date must follow start date." : ""}
          />
          {hasFilters && <Button onClick={reset}>Clear filters</Button>}
        </div>
        <ToolsState
          loading={query.isLoading}
          error={query.isError}
          retry={query.refetch}
          empty={!filtered.length}
          emptyTitle={hasFilters ? "No matching orders" : "No orders yet"}
          emptyMessage={
            hasFilters
              ? "Try a different search, date range or status."
              : "Customer orders will appear here when placed."
          }
          action={hasFilters && <Button onClick={reset}>Clear filters</Button>}
        >
          {mobile ? (
            <div>
              {filtered.slice((page - 1) * 10, page * 10).map((order) => (
                <OrderCard key={order._id || order.orderId} order={order} />
              ))}
            </div>
          ) : (
            <TableContainer>
              <Table aria-label="Tools orders">
                <TableHead>
                  <TableRow>
                    <TableCell>Order / Date</TableCell>
                    <TableCell>Customer</TableCell>
                    <TableCell>Items</TableCell>
                    <TableCell align="right">Total</TableCell>
                    <TableCell>Order status</TableCell>
                    <TableCell>Payment</TableCell>
                    <TableCell align="right">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filtered.slice((page - 1) * 10, page * 10).map((row) => (
                    <TableRow hover key={row._id || row.orderId}>
                      <TableCell>
                        <Typography fontWeight={800} variant="body2">
                          {row.orderId}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {toolsDate(row.createdOn)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {row.shippingAddress?.fullName || "Customer unavailable"}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {row.shippingAddress?.phone || "—"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {row.orderSummary?.itemCount ?? row.items?.length ?? "—"}
                        <Typography variant="caption" display="block" color="text.secondary">
                          {row.orderType || "Purchase"}
                        </Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 800, whiteSpace: "nowrap" }}>
                        {money(orderTotal(row))}
                      </TableCell>
                      <TableCell>
                        <MarketStatus status={row.orderStatus} />
                      </TableCell>
                      <TableCell>
                        <MarketStatus status={row.paymentStatus} payment />
                        {row.receiptUrl && (
                          <Typography variant="caption" display="block" color="text.secondary">
                            Receipt uploaded
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Button
                          size="small"
                          variant={row.orderStatus === "pending" ? "contained" : "outlined"}
                          component={Link}
                          to={`/home/tools-order/${encodeURIComponent(row.orderId)}`}
                        >
                          {row.orderStatus === "pending" ? "Review order" : "View order"}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          <ListFooter page={page} count={filtered.length} pageSize={10} setPage={setPage} />
        </ToolsState>
      </section>
    </ToolsLayout>
  );
}
